/**
 * ERROR 146 — SuCom no muestra nada alusivo a Halloween ni a Día de Muertos.
 *
 * Cualquier búsqueda, enlace de tienda, EAN/SKU, ficha de producto, categoría
 * o pregunta a Rufus que toque esos temas se responde con este mensaje en vez
 * de resultados. El detector es una lista de palabras sobre texto normalizado
 * (minúsculas, sin acentos, sin signos), así que también sirve para los
 * "slugs" de las URLs de tienda (`/disfraz-halloween-bruja/p`).
 */

export const ERROR_146 = {
  code: 146,
  title: "Cristo venció a la muerte.",
  verse: "«Yo soy la resurrección y la vida».",
  reference: "Juan 11:25",
  message: "En SuCom, nuestra esperanza está en Cristo.",
  cta: "Volver a SuCom",
  href: "/",
} as const;

/** Texto plano para Rufus, las apps y los endpoints JSON. */
export const ERROR_146_TEXT = [
  `ERROR ${ERROR_146.code}`,
  ERROR_146.title,
  `${ERROR_146.verse} — ${ERROR_146.reference}`,
  ERROR_146.message,
].join("\n");

/** Cuerpo JSON común para los endpoints que lo devuelven. */
export const ERROR_146_PAYLOAD = {
  code: ERROR_146.code,
  title: ERROR_146.title,
  verse: ERROR_146.verse,
  reference: ERROR_146.reference,
  message: ERROR_146.message,
  text: ERROR_146_TEXT,
  href: ERROR_146.href,
};

// Términos sobre texto normalizado. Se dejaron fuera a propósito palabras
// ambiguas en un comparador de súper ("calabaza", "disfraz", "veladora",
// "papel picado", "diablo" —Casillero del Diablo—): sólo cuentan cuando vienen
// junto a uno de estos términos, que sí son inequívocos.
const PATTERNS: RegExp[] = [
  /\bh+a+l+o+w+e+e*n+\b/, // halloween, haloween, hallowen…
  /\bhallow\b/,
  /\bjalo(?:w|gu|gü)(?:i|ee)n\b/,
  /\bdia(?:s)? de (?:los )?(?:fieles )?(?:muertos?|difuntos)\b/,
  /\bfieles difuntos\b/,
  /\bdifuntos?\b/,
  // "muerto(s)" salvo usos ajenos: sal del Mar Muerto, peso muerto, punto muerto.
  /(?<!\bmar )(?<!\bpeso )(?<!\bpunto )\bmuertos?\b/,
  /\bpan(?:es)? de muertos?\b/,
  /\bsanta muerte\b/,
  /\bcatr[ií]n(?:a|as|es)?\b/,
  /\bcalaver(?:a|as|ita|itas)\b/,
  /\bcalacas?\b/,
  /\bofrendas?\b/,
  /\bcemp[ao][sz]?[uú]?(?:ch|x)[ií]t?l?\b/, // cempasúchil, cempazúchitl…
  /\bcempoalx[oó]chitl\b/,
  /\bmictl[aá]n\b/,
  /\bnoche de brujas\b/,
  /\bbruj(?:a|as|o|os|eria|erias)\b/,
  /\btruco o trato\b/,
  /\btrick or treat\b/,
  /\bzombi(?:e|es|s)?\b/,
  /\bvampir(?:o|os|a|as|esa|esas)\b/,
  /\bfantasmas?\b/,
  /\bmomias?\b/,
  /\besqueletos?\b/,
  /\bhombres? lobo\b/,
  /\bfrankenstein\b/,
  /\bdr[aá]cula\b/,
  /\bjack o lantern\b/,
  /\bspooky\b/,
  /\bsamhain\b/,
  /\bouija\b/,
  /\bla llorona\b/,
];

export function normalizeForError146(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    // URLs: decodificar %20 y similares antes de quitar signos.
    .replace(/%[0-9a-f]{2}/gi, (m) => {
      try {
        return decodeURIComponent(m);
      } catch {
        return " ";
      }
    })
    .replace(/[^a-z0-9ñ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** ¿Alguno de los textos es alusivo a Halloween o Día de Muertos? */
export function isError146(...texts: Array<string | null | undefined>): boolean {
  for (const raw of texts) {
    if (!raw) continue;
    const t = normalizeForError146(raw);
    if (t && PATTERNS.some((re) => re.test(t))) return true;
  }
  return false;
}

/** Producto (o fila de catálogo) alusivo: nombre, marca, categoría, etiquetas. */
export function isError146Product(
  p:
    | {
        name?: string | null;
        brand?: string | null;
        category?: string | null;
        subcategory?: string | null;
        tags?: string[] | null;
      }
    | null
    | undefined,
): boolean {
  if (!p) return false;
  return isError146(p.name, p.brand, p.category, p.subcategory, ...(p.tags ?? []));
}
